import { html, type Html, Page, View } from 'rune-ts';
import klass from './RecentlySold.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '../../components/cells/Footer/constant';
import { ProductCardData, ProductCardOption, ProductCardSize } from '../../../features/ProductList/type';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { EXCLUDE_MAKER_MODULE } from '../../../shared/constant';
import { DocumentVisibilityTracker } from '../../../shared/util/DocumentVisibilityTracker';
import { RECENTLY_SOLD_LIMIT } from '../../../features/ProductList/constant/recently_sold';
import { ItemSoldNowCoverBannerView } from './views/ItemSoldNowCoverBannerView';
import { map, pipe, take, toArray } from '@fxts/core';
import {
  ProductCardList,
  ProductCardListOption,
} from '../../components/cells/ProductCardList/ProductCardList';
import { ProductCard } from '../../components/cells/ProductCard/ProductCard';
import {
  ProductTagAlign,
  ProductTagBaseProps,
  ProductTagOption,
  ProductTagRadius,
  ProductTagSize,
  ProductTagVariant,
} from '../../components/cells/ProductTag/ProductTagBase';
import { getRecentlySoldProductsApi } from '../../../features/ProductList/api';
import { CoreUiS } from '@services/MarppleShop/features/Core/outbound/share.ts';

export type RecentlySoldPageData = {
  recently_sold_products: ProductCardData[];
  layout_options: BaseLayoutOptions;

  /**
   * ms
   */
  polling_duration: number;
  limit: number;
};

class RecentlySoldPageBodyView extends View<RecentlySoldPageData> {
  private ProductCardListView: ProductCardList;

  private visibility_tracker: DocumentVisibilityTracker;

  private readonly ProductCardOption: ProductCardListOption = {
    resize_width: 600,
    limit: this.data.limit,
    size: this.data.layout_options.is_mobile ? ProductCardSize.small : ProductCardSize.large,
    grid: this.data.layout_options.is_mobile
      ? {
          columns: 2,
          column_gap: 16,
          row_gap: 24,
        }
      : {
          columns: 5,
          column_gap: 16,
          row_gap: 32,
        },
  };

  private readonly RealtimeTagOption: ProductTagBaseProps;

  constructor(data: RecentlySoldPageData) {
    super(data);

    this.RealtimeTagOption = {
      lang: ET.lang,
      size: this.data.layout_options.is_mobile ? ProductTagSize.medium : ProductTagSize.large,
      radius: ProductTagRadius.none,
      variant: ProductTagVariant.transparent,
      options: [ProductTagOption.fit_content],
      align: ProductTagAlign.start,
    };

    this.ProductCardListView = new ProductCardList(
      this.generateProductCardViews(data.recently_sold_products),
      this.ProductCardOption,
    );

    this.visibility_tracker = new DocumentVisibilityTracker(
      this.data.polling_duration,
      async (start_timestamp) => {
        const products = await getRecentlySoldProductsApi({
          last_ordered_at: this.ProductCardListView.getLastOrderedAt() ?? new Date(start_timestamp),
        });

        products.forEach((product) => {
          const cardView = this.generateProductCardView(product, {
            is_lazy: false,
            is_hidden: true,
          });
          this.ProductCardListView.prepend(cardView);
        });
        this.ProductCardListView.syncCardViews();
      },
    );
  }

  private generateProductCardViews(products: ProductCardData[]): ProductCard[] {
    return pipe(
      products,
      take(RECENTLY_SOLD_LIMIT.LIST),
      map((p) => this.generateProductCardView(p)),
      toArray,
    );
  }

  private generateProductCardView(
    product: ProductCardData,
    option: Partial<ProductCardOption> = {},
  ): ProductCard {
    return new ProductCard(product, {
      ...this.ProductCardOption,
      ...option,
    }).setRealtimeTagOption(this.RealtimeTagOption);
  }

  protected override template() {
    const { is_mobile } = this.data.layout_options;

    return html`<div class="${klass.body}">
      <!-- title -->
      <div class="${klass.page_title}">
        ${CoreUiS.makeShoppingTitle(is_mobile, 'realtime', ET('mps2::recently_sold::sub_title'))}
      </div>

      <!-- banner -->
      <div class="${klass.banner}">
        ${new ItemSoldNowCoverBannerView({
          is_mobile: this.data.layout_options.is_mobile,
        })}
      </div>

      <!-- products -->
      <div class="${klass.product_cards_container}">${this.ProductCardListView}</div>
    </div>`;
  }

  protected override async onRender() {
    this.visibility_tracker.startTracking();
  }

  protected override onUnmount() {
    this.visibility_tracker.stopTracking();
  }
}

export class RecentlySoldPage extends Page<RecentlySoldPageData> {
  protected override template(): Html {
    const { layout_options } = this.data;

    const body = new RecentlySoldPageBodyView(this.data);

    return html`
      <div class="${klass.page} ${EXCLUDE_MAKER_MODULE}">
        ${new LayoutView(
          {
            body,
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            require_top_arrow_button: true,
            has_bottom_tab_bar: true,
            selected_bottom_tab_idx: 3,
            ...layout_options,
            full_width: layout_options.is_mobile,
          },
        )}
      </div>
    `;
  }
}
